import * as React from "react"
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"
import { XIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { resolveAsChild } from "./as-child"

/**
 * Radix let `DialogContent` veto an outside press via `onInteractOutside`.
 * Base UI surfaces that on the Root's `onOpenChange` event details, so the
 * content registers its guard here and the Root consults it.
 */
const DialogGuardContext = React.createContext<{
  register: (guard: ((event: Event) => boolean) | undefined) => void
} | null>(null)

function Dialog({
  onOpenChange,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const guardRef = React.useRef<((event: Event) => boolean) | undefined>(undefined)
  const register = React.useCallback(
    (guard: ((event: Event) => boolean) | undefined) => {
      guardRef.current = guard
    },
    [],
  )

  const handleOpenChange: NonNullable<
    React.ComponentProps<typeof DialogPrimitive.Root>["onOpenChange"]
  > = (open, eventDetails) => {
    if (!open && eventDetails.reason === "outside-press" && guardRef.current?.(eventDetails.event)) {
      eventDetails.cancel()
      return
    }
    onOpenChange?.(open, eventDetails)
  }

  return (
    <DialogGuardContext.Provider value={{ register }}>
      <DialogPrimitive.Root data-slot="dialog" onOpenChange={handleOpenChange} {...props} />
    </DialogGuardContext.Provider>
  )
}

function DialogTrigger({
  asChild,
  render,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger> & {
  /** Radix's `asChild`, bridged onto Base UI's `render`. */
  asChild?: boolean
}) {
  const resolved = resolveAsChild(asChild, render, props)

  return (
    <DialogPrimitive.Trigger
      data-slot="dialog-trigger"
      render={resolved.render}
      {...resolved.props}
    />
  )
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

// Base UI calls the dimmed layer `Backdrop`.
function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Backdrop>) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className
      )}
      {...props}
    />
  )
}

/**
 * Fires once when the popup mounts. Rendered inside `DialogPrimitive.Popup`,
 * which only mounts while the dialog is open — the wrapper around it mounts
 * even when it is closed, so the effect cannot live there.
 */
function OpenEffect({ onOpen }: { onOpen: (event: Event) => void }) {
  const onOpenRef = React.useRef(onOpen)
  onOpenRef.current = onOpen

  React.useEffect(() => {
    onOpenRef.current(new Event("openautofocus"))
  }, [])

  return null
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  overlayClassName,
  onInteractOutside,
  onOpenAutoFocus,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Popup> & {
  showCloseButton?: boolean
  overlayClassName?: string
  /**
   * Radix-compatible: call `preventDefault()` to keep the dialog open on an
   * outside press. Forwarded to the Root, which owns the decision in Base UI.
   */
  onInteractOutside?: (event: { preventDefault: () => void }) => void
  /** Radix-compatible: fires once when the dialog opens. */
  onOpenAutoFocus?: (event: Event) => void
}) {
  const guard = React.useContext(DialogGuardContext)

  React.useEffect(() => {
    if (!guard) {
      return
    }
    guard.register(
      onInteractOutside
        ? () => {
            let prevented = false
            onInteractOutside({ preventDefault: () => { prevented = true } })
            return prevented
          }
        : undefined,
    )
    return () => guard.register(undefined)
  }, [guard, onInteractOutside])

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          "bg-background data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6  duration-200 outline-none sm:max-w-lg",
          className
        )}
        {...props}
      >
        {onOpenAutoFocus ? <OpenEffect onOpen={onOpenAutoFocus} /> : null}
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="ring-offset-background focus:ring-ring data-open:bg-accent data-open:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>
          Close
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogClose({
  asChild,
  render,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close> & {
  /** Radix's `asChild`, bridged onto Base UI's `render`. */
  asChild?: boolean
}) {
  const resolved = resolveAsChild(asChild, render, props)

  return (
    <DialogPrimitive.Close
      data-slot="dialog-close"
      render={resolved.render}
      {...resolved.props}
    />
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

export { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, };
